import React, { useState, useEffect } from "react";
import { FaRegEye } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import Badge from "@/components/Badges";
import {
  PrimaryButton,
  SecondaryButton,
  DangerButton,
} from "@/components/Buttons";
import Loader from "@/components/Loader";
import ModalBasic from "@/components/ModalBasic";
import FileUpload from "@/components/FileUpload.jsx";
import Papa from "papaparse";

import {
  getIngestionHistory,
  createSyncNowJob,
  cancelJob,
  notifyManualCsvUpload,
} from "@/api/externalFeeds";
import { getFileUploadPresignedUrl } from "@/api/upload";
import { uploadFileToS3 } from "@/api";
import { getStore } from "@/api/settings";
import { getSftpConfig } from "@/api/sftp";
import appConfig from "@/config";
import { addToast } from "@/store/app/action";

/**
 * Build a link to the store's drop folder in the S3 console.
 *
 * The prefix comes from `import_settings.credentials.path`, which is stored
 * without a guaranteed trailing slash and sometimes with a leading one; the
 * console treats "A116" and "A116/" as different places, so normalize both.
 * Slashes are left unescaped to match the console's own URL shape.
 */
function s3ConsoleUrl(credentialsPath) {
  const prefix = String(credentialsPath || "")
    .trim()
    .replace(/^\/+/, "");
  if (!prefix) return null;

  const withSlash = prefix.endsWith("/") ? prefix : `${prefix}/`;
  const region = appConfig.awsRegion;
  const bucket = appConfig.ftpBucket;
  if (!region || !bucket) {
    // Easy to lose when editing src/config/index.js, and the only symptom is a
    // button that quietly never appears — so say so.
    console.warn(
      `[IngestionHistory] No S3 link: config is missing ${
        !bucket ? "ftpBucket" : "awsRegion"
      } for the current environment.`
    );
    return null;
  }

  const encoded = encodeURIComponent(withSlash).replace(/%2F/g, "/");
  return `https://${region}.console.aws.amazon.com/s3/buckets/${bucket}?region=${region}&prefix=${encoded}&showversions=false`;
}

const HISTORY_TYPE_LABELS = {
  automatic_feed: "Automatic Feed",
  manual_upload: "Manual Upload",
  manual_sync: "Manual Sync",
  initial_sync: "Initial Sync",
};

const IngestionHistory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { selectedLocation } = useSelector((state) => state.app);
  const [s3SourceUrl, setS3SourceUrl] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [ingestionHistory, setIngestionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [fullLocationDetail, setFullLocationDetail] = useState(null);
  const [cancellingJobId, setCancellingJobId] = useState(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [jobToCancel, setJobToCancel] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadStep, setUploadStep] = useState(1);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRowCount, setCsvRowCount] = useState(0);
  /** First non-empty data row, one string per column (same order as csvHeaders) */
  const [firstDataRowCells, setFirstDataRowCells] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({});
  const [mappingError, setMappingError] = useState("");
  const [duplicateFields, setDuplicateFields] = useState([]);

  const PRODUCT_SYSTEM_FIELDS = [
    "retailer_upc",
    "retailer_sku",
    "sell_price",
    "quantity",
    "last_sold",
    "item_name",
    "brand",
    "title",
  ];

  const PRODUCT_SYSTEM_FRIENDLY = {
    retailer_upc: "Retailer UPC",
    retailer_sku: "Retailer SKU",
    sell_price: "Sell Price",
    quantity: "Quantity",
    last_sold: "Last Sold Date",
    item_name: "Item Name",
    brand: "Brand",
    title: "Title",
  };

  const REQUIRED_PRODUCT_FIELDS = ["retailer_upc", "sell_price", "quantity"];

  const ORDERED_PRODUCT_FIELDS = [
    ...REQUIRED_PRODUCT_FIELDS,
    ...PRODUCT_SYSTEM_FIELDS.filter((f) => !REQUIRED_PRODUCT_FIELDS.includes(f)),
  ];

  const getCsvColumnForSystemField = (systemField) =>
    Object.entries(fieldMapping).find(([, value]) => value === systemField)?.[0] ||
    "";

  const getExampleForCsvColumn = (csvHeader) => {
    const colIndex = csvHeaders.indexOf(csvHeader);
    if (colIndex === -1) return "";
    return firstDataRowCells[colIndex] ?? "";
  };

  const formatMappingExample = (text, maxLen = 48) => {
    if (!text) return "";
    return text.length > maxLen ? `${text.slice(0, maxLen)}…` : text;
  };

  useEffect(() => {
    const fetchLocationDetails = async () => {
      try {
        const res = await getStore();
        setFullLocationDetail(res.store);
      } catch (error) {
        console.error("Failed to fetch location details:", error);
      }
    };
    fetchLocationDetails();
  }, [selectedLocation]);

  // The drop-folder prefix lives on `import_settings`, which the store endpoint
  // strips, so it needs its own read.
  const sourceStoreId = selectedLocation?.store_id;

  useEffect(() => {
    const storeId = sourceStoreId;
    if (!storeId) {
      setS3SourceUrl(null);
      console.log("No store ID found");
      return;
    }
    let cancelled = false;

    getSftpConfig(storeId)
      .then((res) => {
        if (cancelled) return;
        const payload =
          res && typeof res === "object" && !Array.isArray(res) ? res : null;
        const settings = payload?.import_settings ?? payload;
        console.log(`settings: ${settings?.credentials?.path}`);
        setS3SourceUrl(s3ConsoleUrl(settings?.credentials?.path));
      })
      .catch(() => {
        // No SFTP config for this store, or the route isn't deployed — either
        // way there is no source folder to link to.\
        console.log("No SFTP config for this store, or the route isn't deployed — either way there is no source folder to link to.");
        if (!cancelled) setS3SourceUrl(null);
      });

    return () => {
      cancelled = true;
    };
  }, [sourceStoreId]);

  const fetchHistory = async (silent = false) => {
    if (!selectedLocation) return;

    if (!silent) {
      setLoading(true);
    }
    try {
      const response = await getIngestionHistory(currentPage, itemsPerPage, selectedLocation?.store_id);
      if (response.success) {
        const mappedHistory = response.history.map((item) => {
          const originalStatus = item.status;
          const displayStatus =
            originalStatus === "success"
              ? "Success"
              : originalStatus === "failed"
              ? "Failed"
              : originalStatus === "in_progress"
              ? "In Progress"
              : originalStatus === "queued"
              ? "Queued"
              : originalStatus === "cancelled"
              ? "Cancelled"
              : originalStatus;

          return {
            id: item.id,
            type: HISTORY_TYPE_LABELS[item.type] || item.type,
            dateStarted: item.date_started,
            dateCompleted: item.date_completed || null,
            status: displayStatus,
            originalStatus: originalStatus,
            jobId: item.job_id || item.id,
            itemsProcessed: item.items_processed || 0,
            feedName: item.feed_name || "",
            provider: item.provider || "",
            totalItemCount: item.total_item_count || 0,
            newItemsCount: item.new_items_count || 0,
            newMappedCount: item.new_mapped_count || 0,
            newUnmappedCount: item.new_unmapped_count || 0,
          };
        });
        setIngestionHistory(mappedHistory);
        setTotalCount(response.total || 0);
        setTotalPages(response.totalPages || 0);
      }
    } catch (error) {
      console.error("Failed to fetch ingestion history:", error);
      dispatch(
        addToast({
          type: "error",
          message: "Failed to load ingestion history",
          timer: 5,
        })
      );
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, selectedLocation, dispatch]);

  useEffect(() => {
    const hasActiveJobs = ingestionHistory.some(
      (item) =>
        item.originalStatus === "in_progress" || item.originalStatus === "queued"
    );

    if (!selectedLocation || !hasActiveJobs) {
      return;
    }

    const intervalId = setInterval(() => {
      fetchHistory(true);
    }, 5000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingestionHistory, selectedLocation]);

  const handleManualUploadClick = () => {
    setUploadModalOpen(true);
    setUploadStep(1);
    setUploadedFiles([]);
    setCsvHeaders([]);
    setCsvRowCount(0);
    setFirstDataRowCells([]);
    setFieldMapping({});
    setMappingError("");
    setDuplicateFields([]);
  };

  const handleFileChange = (files) => {
    setUploadedFiles(files);
    if (files && files[0]) {
      Papa.parse(files[0], {
        complete: function (results) {
          const headers = results.data[0] || [];
          setCsvHeaders(headers);
          const initialMapping = {};
          headers.forEach((h) => {
            initialMapping[h] = "";
          });
          setFieldMapping(initialMapping);

          const dataRows = results.data
            .slice(1)
            .filter(
              (row) =>
                Array.isArray(row) &&
                row.some(
                  (cell) =>
                    cell !== undefined &&
                    cell !== null &&
                    String(cell).trim() !== ""
                )
            );
          setCsvRowCount(dataRows.length);

          const firstRow = dataRows[0] || [];
          const sampleCells = headers.map((_, i) => {
            const cell = firstRow[i];
            if (cell === undefined || cell === null) return "";
            return String(cell).trim();
          });
          setFirstDataRowCells(sampleCells);
        },
        skipEmptyLines: true,
      });
    } else {
      setCsvHeaders([]);
      setFieldMapping({});
      setCsvRowCount(0);
      setFirstDataRowCells([]);
    }
  };

  const handleSystemFieldMappingChange = (systemField, csvHeader) => {
    setFieldMapping((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((csvField) => {
        if (next[csvField] === systemField) {
          next[csvField] = "";
        }
      });
      if (csvHeader) {
        next[csvHeader] = systemField;
      }
      return next;
    });
  };

  const handleUploadCancel = () => {
    setUploadModalOpen(false);
    setUploadStep(1);
    setUploadedFiles([]);
    setCsvHeaders([]);
    setCsvRowCount(0);
    setFirstDataRowCells([]);
    setFieldMapping({});
    setMappingError("");
    setDuplicateFields([]);
  };

  const handleSyncNow = async () => {
    if (!fullLocationDetail?.api_credentials?.provider) {
      dispatch(
        addToast({
          type: "error",
          message:
            "No POS connection found. Please connect a POS system first.",
          timer: 5,
        })
      );
      return;
    }

    setSyncing(true);
    try {
      const response = await createSyncNowJob();
      if (response.success) {
        dispatch(
          addToast({
            type: "success",
            message: "Sync job created successfully",
            timer: 5,
          })
        );
        setCurrentPage(1);
        setTimeout(() => {
          fetchHistory();
        }, 500);
      }
    } catch (error) {
      console.error("Failed to create sync job:", error);
      dispatch(
        addToast({
          type: "error",
          message:
            error.response?.data?.error || "Failed to create sync job",
          timer: 5,
        })
      );
    } finally {
      setSyncing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Success":
        return "green";
      case "Failed":
        return "red";
      case "In Progress":
        return "yellow";
      case "Queued":
        return "blue";
      case "Cancelled":
        return "gray";
      default:
        return "gray";
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "Automatic Feed":
        return "blue";
      case "Manual Upload":
        return "red";
      default:
        return "bg-slate-100 text-slate-600";
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = ingestionHistory;

  const handleViewDetails = (item) => {
    const id = item?.jobId || item?.id;
    if (!id) return;
    navigate(`/external-feeds/ingestion/${id}`);
  };

  const handleCancelClick = (item) => {
    setJobToCancel(item);
    setCancelModalOpen(true);
  };

  const handleCancelJob = async () => {
    if (!jobToCancel) return;

    setCancellingJobId(jobToCancel.jobId || jobToCancel.id);
    try {
      const response = await cancelJob(jobToCancel.jobId || jobToCancel.id);
      if (response.success) {
        dispatch(
          addToast({
            type: "success",
            message: "Job cancelled successfully",
            timer: 5,
          })
        );
        setCancelModalOpen(false);
        setJobToCancel(null);
        setTimeout(() => {
          fetchHistory();
        }, 500);
      }
    } catch (error) {
      console.error("Failed to cancel job:", error);
      dispatch(
        addToast({
          type: "error",
          message:
            error.response?.data?.error || "Failed to cancel job",
          timer: 5,
        })
      );
    } finally {
      setCancellingJobId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 relative overflow-x-auto">
        <header className="px-5 py-4 border-b border-slate-100 dark:border-slate-700">
          <div className="flex flex-wrap justify-between items-center">
            <h2 className="font-semibold text-slate-800 dark:text-slate-100">
              Ingestion History
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <PrimaryButton
                onClick={handleSyncNow}
                disabled={syncing || !fullLocationDetail?.api_credentials?.provider}
                loading={syncing}
              >
                <span className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 fill-current opacity-50 shrink-0"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 3a5 5 0 1 1-4.546 2.914.75.75 0 1 0-1.342-.67A6.5 6.5 0 1 0 8 1.5V0a.75.75 0 0 0-1.5 0v3A.75.75 0 0 0 7.25 4.5h3a.75.75 0 0 0 0-1.5H8z" />
                  </svg>
                  <span className="hidden xs:block">Sync Now</span>
                </span>
              </PrimaryButton>
              {s3SourceUrl && (
                <a
                  href={s3SourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn bg-emerald-500 hover:bg-emerald-600 text-white"
                  title="Open this store's drop folder in the S3 console"
                >
                  <svg
                    className="w-4 h-4 fill-current opacity-50 shrink-0"
                    viewBox="0 0 16 16"
                  >
                    <path d="M9 0v2h3.6L6.8 7.8l1.4 1.4L14 3.4V7h2V0H9zM2 2h4V0H2a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4h-2v4H2V2z" />
                  </svg>
                  <span className="hidden xs:block ml-2">View S3 Source</span>
                </a>
              )}
            </div>
          </div>
        </header>
        <div>
          <div className="overflow-x-auto">
            <table className="table-auto w-full dark:text-slate-300 divide-y divide-slate-200 dark:divide-slate-700">
              <thead className="text-xs uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Type</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Date Started</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">
                      Date Completed
                    </div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Status</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">
                      Total Items
                    </div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">
                      New Items
                    </div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">
                      New Mapped
                    </div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">
                      New Unmapped
                    </div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Actions</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="10"
                      className="px-2 first:pl-5 last:pr-5 py-8 text-center"
                    >
                      <Loader />
                      <p className="mt-4 text-slate-600 dark:text-slate-300">
                        Loading history...
                      </p>
                    </td>
                  </tr>
                ) : currentItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan="10"
                      className="px-2 first:pl-5 last:pr-5 py-8 text-center"
                    >
                      <p className="text-slate-600 dark:text-slate-300">
                        No ingestion history found
                      </p>
                    </td>
                  </tr>
                ) : (
                  currentItems.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-slate-50 dark:hover:bg-slate-700/50"
                    >
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <Badge color={getTypeColor(item.type)}>
                          {item.type}
                        </Badge>
                      </td>

                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="text-slate-800 dark:text-slate-100">
                          {item.dateStarted
                            ? new Date(item.dateStarted).toLocaleString()
                            : "-"}
                        </div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="text-slate-800 dark:text-slate-100">
                          {item.dateCompleted
                            ? new Date(item.dateCompleted).toLocaleString()
                            : "-"}
                        </div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <Badge color={getStatusColor(item.status)}>
                          {item.status}
                        </Badge>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="text-slate-800 dark:text-slate-100">
                          {item.totalItemCount.toLocaleString()}
                        </div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="text-slate-800 dark:text-slate-100">
                          {item.newItemsCount.toLocaleString()}
                        </div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="text-slate-800 dark:text-slate-100">
                          <span className="text-green-600 dark:text-green-400">
                            {item.newMappedCount.toLocaleString()}
                          </span>
                        </div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="text-slate-800 dark:text-slate-100">
                          <span className="text-orange-600 dark:text-orange-400">
                            {item.newUnmappedCount.toLocaleString()}
                          </span>
                        </div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            className="text-slate-400 hover:text-slate-500 dark:text-slate-500 dark:hover:text-slate-400"
                            onClick={() => handleViewDetails(item)}
                            title="View Details"
                          >
                            <FaRegEye className="w-5 h-5" />
                          </button>
                          {item.originalStatus === "queued" && (
                            <button
                              className="text-red-400 hover:text-red-500 dark:text-red-500 dark:hover:text-red-400 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleCancelClick(item)}
                              disabled={
                                cancellingJobId === (item.jobId || item.id)
                              }
                              title="Cancel Job"
                            >
                              {cancellingJobId === (item.jobId || item.id) ? (
                                <Loader />
                              ) : (
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="mt-2 flex items-center justify-between">
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Showing {indexOfFirstItem + 1} to{" "}
            {Math.min(indexOfLastItem, totalCount)} of {totalCount} results
          </div>
          <div className="flex items-center space-x-2">
            <button
              className="btn bg-white dark:bg-theme-surface-accent border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-500 dark:text-slate-400 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={currentPage === 1 || loading}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Previous
            </button>
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Page {currentPage} of {totalPages}
            </div>
            <button
              className="btn bg-white dark:bg-theme-surface-accent border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-500 dark:text-slate-400 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={currentPage === totalPages || loading}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default IngestionHistory;

